import { useRef, useState } from "react";
import { Camera, CheckCircle2, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PhotoCapture } from "../photo/PhotoCapture";
import { API_BASE } from "../../lib/api";
import { rpc } from "../../lib/rpc";
import { Button } from "../ui/button";

/**
 * FlowPhotoSlot -- deklarativer Foto-Slot für Mobile-Flows.
 *
 * Properties:
 *   label            -- Sichtbares Label (z.B. "Lieferschein-Foto")
 *   entityType       -- 'movement' | 'unknown_item_case' | 'article' | ...
 *   purpose          -- siehe models.py PhotoPurpose
 *   required         -- Pflicht? (Submit-Guard durch Parent)
 *   value            -- current photoId (controlled)
 *   onChange(photoId) -- wird bei Capture / Clear gerufen
 *   helpText         -- optionaler Zusatztext
 *   isEvidence       -- Auto-true für purpose=disposal_evidence/damage/...
 *
 * Gibt Thumbnail-Preview + Neu-Aufnehmen + Entfernen.
 */

const EVIDENCE_PURPOSES = new Set([
    "disposal_evidence",
    "damage",
    "inventory_difference",
    "return_condition",
    "maintenance",
]);

export function FlowPhotoSlot({
    label = "Foto",
    entityType = "movement",
    purpose = "other",
    entityId = null,
    required = false,
    value = null,
    onChange,
    helpText,
    isEvidence,
    requireSync,
    sessionKey,
    testId,
}) {
    const tid = testId || `photo-slot-${purpose}`;
    const evidence = isEvidence !== undefined ? isEvidence : EVIDENCE_PURPOSES.has(purpose);
    const sync = requireSync !== undefined ? requireSync : (required || evidence);
    const [removing, setRemoving] = useState(false);
    // Compare the capture's render with the live session, including uploads
    // that finish after another capture has already started in that session.
    const sessionKeyRef = useRef(sessionKey);
    sessionKeyRef.current = sessionKey;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const captureEntryRef = useRef(null);

    const handleCaptured = (photoId, queueEntryId, isLate) => {
        if (sessionKey !== sessionKeyRef.current) return;
        if (!isLate) {
            captureEntryRef.current = queueEntryId;
            onChangeRef.current?.(photoId);
            return;
        }
        if (captureEntryRef.current !== queueEntryId) return;
        onChangeRef.current?.(photoId);
    };

    const clear = async () => {
        captureEntryRef.current = null;
        if (!value) {
            onChange?.(null);
            return;
        }
        setRemoving(true);
        const res = await rpc.del(`/photos/${value}`);
        setRemoving(false);
        if (!res.ok) {
            toast.error(res.error || "Konnte Foto nicht entfernen.");
            return;
        }
        onChange?.(null);
    };

    return (
        <div className="flex flex-col gap-2 rounded-lg border border-border/60 bg-card/30 p-3" data-testid={tid}>
            <div className="flex items-center gap-2">
                <Camera className="h-4 w-4 text-primary" />
                <span className="text-xs font-medium text-foreground">
                    {label}
                    {required ? <span className="ml-1 text-destructive">*</span> : null}
                </span>
                {value ? (
                    <span className="ml-auto inline-flex items-center gap-1 text-xs uppercase tracking-wider text-secondary">
                        <CheckCircle2 className="h-3 w-3" /> gesetzt
                    </span>
                ) : null}
            </div>

            {value ? (
                <div className="flex items-center gap-3">
                    <img
                        src={`${API_BASE}/photos/${value}/thumb`}
                        alt={label}
                        className="h-16 w-16 rounded-md border border-border bg-card/40 object-contain"
                        data-testid={`${tid}-thumb`}
                    />
                    <div className="flex-1 text-xs text-muted-foreground">
                        ID: <code>{value.slice(0, 8)}…</code>
                    </div>
                    <PhotoCapture
                        entityType={entityType}
                        entityId={entityId}
                        purpose={purpose}
                        isEvidence={evidence}
                        requireSync={sync}
                        onCaptured={handleCaptured}
                        testId={`${tid}-retake`}
                        renderTrigger={() => (
                            <Button variant="outline" size="sm" data-testid={`${tid}-retake`}>
                                <RefreshCw className="mr-1 h-3 w-3" /> Neu
                            </Button>
                        )}
                    />
                    <Button variant="ghost" size="sm" onClick={clear} disabled={removing} aria-label="Foto entfernen" data-testid={`${tid}-clear`}>
                        <Trash2 className="h-3 w-3" />
                    </Button>
                </div>
            ) : (
                <PhotoCapture
                    entityType={entityType}
                    entityId={entityId}
                    purpose={purpose}
                    isEvidence={evidence}
                    requireSync={sync}
                    onCaptured={handleCaptured}
                    testId={`${tid}-capture`}
                    renderTrigger={() => (
                        <Button
                            type="button"
                            variant="outline"
                            className="pw-tap w-full justify-start gap-3 border-dashed border-border/70 text-foreground hover:border-primary"
                            data-testid={`${tid}-capture`}
                        >
                            <Camera className="h-4 w-4 text-primary" />
                            <span className="text-sm">Foto aufnehmen</span>
                        </Button>
                    )}
                />
            )}

            {helpText ? (
                <p className="text-xs text-muted-foreground">{helpText}</p>
            ) : null}
        </div>
    );
}
